/* QuickNoteDialog — picks a pre-saved or ad-hoc «έτοιμο σχόλιο» +
   one or more printers and fires POST /quick-notes/print.

   Does NOT persist the comment on the table — it's a one-off slip
   that lands in the FTEXT outbox for the companion print agent.

   Shared between desktop (Restaurant.jsx → «Εργασίες → Σχόλια») and
   mobile (MobileRestaurant.jsx → tasks dialog → «Σχόλια»).
*/
import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Printer, Check } from "lucide-react";

export default function QuickNoteDialog({ open, onOpenChange, table }) {
  const [notes, setNotes] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [adhoc, setAdhoc] = useState("");
  const [printerIds, setPrinterIds] = useState(new Set());
  const [busy, setBusy] = useState(false);

  // Load notes + printers whenever the dialog opens (cheap, allows
  // catalog edits to reflect without re-mount).
  useEffect(() => {
    if (!open) return;
    setSelectedNoteId(null);
    setAdhoc("");
    setPrinterIds(new Set());
    (async () => {
      try {
        const [n, p] = await Promise.all([
          http.get("/quick-notes"),
          http.get("/printers"),
        ]);
        setNotes((n.data || []).filter((x) => x.is_active !== false));
        setPrinters((p.data || []).filter((x) => x.active !== false));
      } catch {
        toast.error("Αποτυχία φόρτωσης σχολίων / εκτυπωτών");
      }
    })();
  }, [open]);

  const togglePrinter = (id) => {
    setPrinterIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const finalText = useMemo(() => {
    if (adhoc.trim()) return adhoc.trim();
    const n = notes.find((x) => x.id === selectedNoteId);
    return n?.text || "";
  }, [adhoc, selectedNoteId, notes]);

  // Pick top-3 recently-used (usage_count > 0, sorted by last_used_at desc).
  // Filtered against the «all» list later to avoid duplicate rendering.
  const recentNotes = useMemo(() => {
    const used = notes.filter((n) => (n.usage_count || 0) > 0 && n.last_used_at);
    used.sort((a, b) => (b.last_used_at || "").localeCompare(a.last_used_at || ""));
    return used.slice(0, 3);
  }, [notes]);
  const recentIds = useMemo(() => new Set(recentNotes.map((n) => n.id)), [recentNotes]);

  const canSubmit = finalText.length > 0 && printerIds.size > 0 && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const payload = {
        table_id: table?.id || null,
        printer_ids: Array.from(printerIds),
      };
      if (adhoc.trim()) payload.text = adhoc.trim();
      else payload.note_id = selectedNoteId;
      const r = await http.post("/quick-notes/print", payload);
      const n = r.data?.count || 0;
      toast.success(`🖨️ Στάλθηκε σχόλιο σε ${n} εκτυπωτή${n === 1 ? "" : "ς"}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία εκτύπωσης");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg p-0 gap-0"
        data-testid="quicknote-dialog"
        // Lock dismissal to the Άκυρο / Εκτύπωση buttons. Tapping
        // outside or pressing Escape used to close the dialog mid-edit
        // and the operator lost the typed comment / printer selection.
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="px-4 py-3 border-b bg-blue-50">
          <DialogTitle className="text-base flex items-center gap-2">
            <Printer size={16} /> Σχόλιο · {table?.name || "—"}
          </DialogTitle>
        </DialogHeader>
        <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
          {recentNotes.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-amber-700 font-bold mb-1.5 flex items-center gap-1">
                ⭐ Πρόσφατα
              </div>
              <div className="grid grid-cols-1 gap-1.5">
                {recentNotes.map((n) => {
                  const active = selectedNoteId === n.id && !adhoc.trim();
                  return (
                    <button
                      key={`recent-${n.id}`}
                      onClick={() => { setSelectedNoteId(n.id); setAdhoc(""); }}
                      className={`text-left px-3 py-2 rounded-md border-2 text-sm transition-all active:scale-[0.98] ${
                        active
                          ? "bg-amber-600 text-white border-amber-700 font-bold"
                          : "bg-amber-50 border-amber-300 text-amber-900"
                      }`}
                      data-testid={`qn-recent-${n.id}`}
                    >
                      {n.text}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">
              Έτοιμα Σχόλια ({notes.length})
            </div>
            {notes.length === 0 ? (
              <div className="text-xs italic text-slate-400">
                Δεν υπάρχουν έτοιμα σχόλια. Προσθέστε από Ρυθμίσεις → Έτοιμα Σχόλια.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-1.5">
                {notes.filter((n) => !recentIds.has(n.id)).map((n) => {
                  const active = selectedNoteId === n.id && !adhoc.trim();
                  return (
                    <button
                      key={n.id}
                      onClick={() => { setSelectedNoteId(n.id); setAdhoc(""); }}
                      className={`text-left px-3 py-2 rounded-md border-2 text-sm transition-all active:scale-[0.98] ${
                        active
                          ? "bg-blue-600 text-white border-blue-700 font-bold"
                          : "bg-white border-slate-300 text-slate-800"
                      }`}
                      data-testid={`qn-option-${n.id}`}
                    >
                      {n.text}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">
              ή Ελεύθερο Κείμενο
            </div>
            <Textarea
              value={adhoc}
              onChange={(e) => { setAdhoc(e.target.value); if (e.target.value.trim()) setSelectedNoteId(null); }}
              placeholder="Γράψτε το σχόλιό σας εδώ..."
              rows={3}
              data-testid="qn-adhoc"
            />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">
              Εκτυπωτές ({printerIds.size}/{printers.length})
            </div>
            {printers.length === 0 ? (
              <div className="text-xs italic text-slate-400">
                Δεν υπάρχουν ενεργοί εκτυπωτές.
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-1.5">
                {printers.map((p) => {
                  const on = printerIds.has(p.id);
                  return (
                    <button
                      key={p.id}
                      onClick={() => togglePrinter(p.id)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-md border-2 text-sm font-bold transition-all active:scale-[0.98] ${
                        on
                          ? "bg-emerald-600 text-white border-emerald-700"
                          : "bg-white border-slate-300 text-slate-800"
                      }`}
                      data-testid={`qn-printer-${p.id}`}
                    >
                      {on ? <Check size={14} /> : <Printer size={14} />}
                      <span className="truncate">{p.name}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="px-4 py-3 border-t bg-slate-50 grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="qn-cancel"
          >Άκυρο</Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="qn-submit"
          >
            <Printer size={14} className="mr-1.5" />
            Εκτύπωση
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
